import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  AlertCircle,
  ArrowLeft,
  Award,
  Building2,
  Layers,
  Mail,
  MapPin,
  Pencil,
  ShieldCheck,
  UserCircle,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { AppSidebar } from "@/components/AppSidebar";
import { MobileNavTrigger } from "@/components/MobileTopBar";
import { SidebarProvider } from "@/components/ui/sidebar";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { getUser, updateName } from "@/services/userLogin";
import { getProfile } from "@/services/profileApi";
import { ACCESS_LABELS, ALL_ACCESS } from "@/lib/adminConstants";

const getInitials = (name: string) =>
  name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);

// Render the "*" wildcard grant as "All access" in the read-only chips.
const displayAccess = (values: string[]) =>
  values.includes(ALL_ACCESS) ? [ACCESS_LABELS[ALL_ACCESS]] : values;

const Profile = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const user = getUser();
  const userId = user?.id != null ? String(user.id) : "";

  const [displayName, setDisplayName] = useState<string>(user?.name ?? "");
  const [editOpen, setEditOpen] = useState(false);

  const handleNameSaved = (name: string) => {
    setDisplayName(name);
    setEditOpen(false);
    toast({ title: "Name updated" });
  };

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ["profile", userId],
    queryFn: () => getProfile(userId),
    enabled: !!userId,
  });

  return (
    <SidebarProvider>
      <div className="min-h-dvh flex w-full bg-background">
        <AppSidebar />
        <div className="flex-1 flex flex-col min-w-0 h-dvh overflow-hidden">
          <header className="pt-safe flex shrink-0 items-center gap-2 border-b border-border px-2 py-2 sm:gap-3 sm:px-6 sm:py-3">
            <MobileNavTrigger />
            <div className="flex items-center gap-2.5 min-w-0">
              <UserCircle className="h-5 w-5 text-primary shrink-0" aria-hidden />
              <h1 className="text-base font-semibold text-foreground">My profile</h1>
            </div>
            <Button
              variant="ghost"
              size="sm"
              className="ml-auto gap-1.5 text-muted-foreground"
              onClick={() => navigate(-1)}
            >
              <ArrowLeft className="h-4 w-4" />
              Back
            </Button>
          </header>

          <main className="flex-1 overflow-auto p-4 sm:p-8">
            <div className="mx-auto max-w-3xl space-y-6 motion-safe:animate-in motion-safe:fade-in-50 motion-safe:duration-300">
              {/* Identity */}
              <section className="flex items-center gap-5 rounded-xl border border-border bg-secondary/40 p-6">
                <Avatar className="h-16 w-16 ring-2 ring-primary/20">
                  <AvatarFallback className="bg-primary text-primary-foreground text-xl font-semibold">
                    {getInitials(displayName)}
                  </AvatarFallback>
                </Avatar>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <h2 className="text-2xl font-semibold leading-tight text-foreground truncate">
                      {displayName}
                    </h2>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 shrink-0 text-muted-foreground hover:text-accent-foreground"
                      title="Edit name"
                      aria-label="Edit name"
                      onClick={() => setEditOpen(true)}
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                  <p className="mt-0.5 flex items-center gap-1.5 text-sm text-muted-foreground truncate">
                    <Mail className="h-3.5 w-3.5 shrink-0" aria-hidden />
                    {user?.email}
                  </p>
                </div>
                {user?.role && (
                  <Badge variant="secondary" className="shrink-0 capitalize">
                    {user.role}
                  </Badge>
                )}
              </section>

              {isError ? (
                <div className="flex flex-col items-center justify-center rounded-xl border border-destructive/30 bg-destructive/5 py-16 text-center">
                  <AlertCircle className="mb-3 h-6 w-6 text-destructive" aria-hidden />
                  <p className="text-sm text-muted-foreground">
                    {(error as Error)?.message || "Couldn’t load your profile."}
                  </p>
                  <Button variant="outline" onClick={() => refetch()} className="mt-4">
                    Try again
                  </Button>
                </div>
              ) : isLoading ? (
                <div className="grid gap-6 md:grid-cols-2">
                  <PanelSkeleton />
                  <PanelSkeleton />
                </div>
              ) : data ? (
                <div className="grid gap-6 md:grid-cols-2">
                  <Panel title="Employee details">
                    <Field icon={Building2} label="Department">
                      <ChipList values={data.emp_dept} />
                    </Field>
                    <Field icon={MapPin} label="Office">
                      <ChipList values={data.emp_office} />
                    </Field>
                    <Field icon={Award} label="Grade">
                      {data.emp_grade ? (
                        <span className="text-sm font-medium text-foreground">{data.emp_grade}</span>
                      ) : (
                        <Empty />
                      )}
                    </Field>
                    <Field icon={ShieldCheck} label="Employee">
                      <Badge
                        variant={data.is_employee ? "secondary" : "outline"}
                        className="font-normal"
                      >
                        {data.is_employee ? "Yes" : "No"}
                      </Badge>
                    </Field>
                  </Panel>

                  <Panel
                    title="Data access"
                    subtitle="What you’re allowed to view across the dashboards."
                  >
                    <Field icon={Building2} label="Departments">
                      <ChipList
                        values={displayAccess(data.emp_dept_permission)}
                        emptyLabel="No access"
                      />
                    </Field>
                    <Field icon={MapPin} label="Locations">
                      <ChipList
                        values={displayAccess(data.emp_office_location_permission)}
                        emptyLabel="No access"
                      />
                    </Field>
                    <Field icon={Layers} label="Grades">
                      <ChipList
                        values={displayAccess(data.emp_grade_permission)}
                        emptyLabel="No access"
                      />
                    </Field>
                  </Panel>
                </div>
              ) : null}
            </div>
          </main>
        </div>
      </div>

      <EditNameDialog
        open={editOpen}
        currentName={displayName}
        onOpenChange={setEditOpen}
        onSaved={handleNameSaved}
        onError={(msg) =>
          toast({ variant: "destructive", title: "Couldn’t update name", description: msg })
        }
      />
    </SidebarProvider>
  );
};

function EditNameDialog({
  open,
  currentName,
  onOpenChange,
  onSaved,
  onError,
}: {
  open: boolean;
  currentName: string;
  onOpenChange: (open: boolean) => void;
  onSaved: (name: string) => void;
  onError: (message: string) => void;
}) {
  const [name, setName] = useState(currentName);
  const [saving, setSaving] = useState(false);

  const trimmed = name.trim();
  const isValid = trimmed.length > 0 && trimmed.length <= 100;
  const isDirty = trimmed !== currentName.trim();

  const handleOpenChange = (next: boolean) => {
    if (next) setName(currentName);
    onOpenChange(next);
  };

  const handleSave = async () => {
    if (!isValid || !isDirty || saving) return;
    setSaving(true);
    try {
      const updated = await updateName(trimmed);
      onSaved(updated);
    } catch (e) {
      onError((e as Error).message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Edit name</DialogTitle>
          <DialogDescription>This is the name shown across Prism.</DialogDescription>
        </DialogHeader>

        <form
          className="space-y-2 py-1"
          onSubmit={(e) => {
            e.preventDefault();
            handleSave();
          }}
        >
          <Label htmlFor="profile-name">Name</Label>
          <Input
            id="profile-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Your name"
            autoFocus
            maxLength={100}
          />
        </form>

        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="outline" onClick={() => handleOpenChange(false)} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={!isValid || !isDirty || saving}>
            {saving ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Panel({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
}) {
  return (
    <section className="rounded-xl border border-border bg-card">
      <div className="border-b border-border px-5 py-3.5">
        <h3 className="text-sm font-semibold text-foreground">{title}</h3>
        {subtitle && <p className="mt-0.5 text-xs text-muted-foreground">{subtitle}</p>}
      </div>
      <dl className="divide-y divide-border">{children}</dl>
    </section>
  );
}

function Field({
  icon: Icon,
  label,
  children,
}: {
  icon: React.ComponentType<{ className?: string; "aria-hidden"?: boolean }>;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex items-start justify-between gap-4 px-5 py-3.5">
      <dt className="flex items-center gap-2 text-sm text-muted-foreground">
        <Icon className="h-4 w-4 text-muted-foreground/70" aria-hidden />
        {label}
      </dt>
      <dd className="flex flex-wrap justify-end gap-1.5 text-right">{children}</dd>
    </div>
  );
}

function ChipList({ values, emptyLabel }: { values: string[]; emptyLabel?: string }) {
  if (!values || values.length === 0) return <Empty label={emptyLabel} />;
  return (
    <>
      {values.map((v) => (
        <Badge key={v} variant="secondary" className="font-normal">
          {v}
        </Badge>
      ))}
    </>
  );
}

function Empty({ label }: { label?: string }) {
  return <span className="text-sm text-muted-foreground">{label ?? "—"}</span>;
}

function PanelSkeleton() {
  return (
    <section className="rounded-xl border border-border bg-card">
      <div className="border-b border-border px-5 py-3.5">
        <Skeleton className="h-4 w-32" />
      </div>
      <div className="divide-y divide-border">
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={i} className="flex items-center justify-between px-5 py-3.5">
            <Skeleton className="h-4 w-24" />
            <Skeleton className="h-5 w-28" />
          </div>
        ))}
      </div>
    </section>
  );
}

export default Profile;
